import { useState, useRef, useEffect } from 'react';
import {
  Search, Play, Receipt, CreditCard, TrendingDown, GraduationCap,
  Users, Shield, Lock, TrendingUp, Clock, BarChart2, ClipboardList,
  Settings, LogOut, Calendar, X, UserCheck, User, Send, Sparkles,
  ChevronRight, Wrench,
} from 'lucide-react';
import { useAssistantParser } from '../../hooks/useAssistantParser';
import { formatClassLevel } from '../../utils/classLevelUtils';
import './SearchCommandPalette.css';

/** Proposal icon names → components; shared with the Ctrl/⌘K CommandPalette. */
export const ICON_MAP = {
  Play, Receipt, CreditCard, TrendingDown, GraduationCap,
  Users, Shield, Lock, TrendingUp, Clock, BarChart2, ClipboardList,
  Settings, LogOut, Calendar, X, UserCheck, User, Send, Sparkles, Wrench, ChevronRight,
};

/**
 * AI-like assistant bar — understands natural French commands.
 * Single intelligence layer: useAssistantParser (NLP pattern matching).
 * No LLM. No fallback command registry.
 */
const SearchCommandPalette = ({
  families = [],
  students = [],
  stats = {},
  onAction,
  onProposal,
  onQueryChange,
  placeholder,
  variant = 'hero',
  controlledQuery,
  forceOpen = false,
  disableGlobalShortcut = false,
  toolOnly = false,
}) => {
  const [internalQuery, setInternalQuery] = useState('');
  const isControlled = controlledQuery !== undefined;
  const query = isControlled ? controlledQuery : internalQuery;
  const setQuery = (v) => {
    if (!isControlled) setInternalQuery(v);
    onQueryChange?.(v);
  };
  const [openState, setOpenState] = useState(false);
  const open = forceOpen || openState;
  const setOpen = (v) => { if (!forceOpen) setOpenState(v); };
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const proposals = useAssistantParser(query, families, students, stats, toolOnly);
  const hasResults = proposals.length > 0;

  // Build flat list for keyboard nav
  const flatItems = useRef([]);
  useEffect(() => {
    flatItems.current = proposals.map(p => ({ type: 'proposal', ref: p }));
    setActiveIdx(-1);
  }, [proposals]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Global Ctrl+K shortcut (opt-out for demo/preview embeds)
  useEffect(() => {
    if (disableGlobalShortcut) return undefined;
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, disableGlobalShortcut]);

  const handleProposalClick = (proposal) => {
    onProposal?.(proposal);
    setQuery('');
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || !hasResults) return;
    const max = flatItems.current.length;
    if (!max) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => (i + 1) % max);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => (i - 1 + max) % max);
    } else if (e.key === 'Enter' && activeIdx >= 0 && activeIdx < max) {
      e.preventDefault();
      handleProposalClick(flatItems.current[activeIdx].ref);
    }
  };

  const closePalette = () => { setQuery(''); setOpen(false); setActiveIdx(-1); };

  return (
    <div ref={wrapRef} className={`scp scp--${variant}${open ? ' scp--open' : ''}`}>
      {/* Backdrop (mobile full-screen mode) */}
      {open && <div className="scp__backdrop" onClick={closePalette} />}

      <div className="scp__input-wrap" onClick={() => { if (!isControlled) { inputRef.current?.focus(); setOpen(true); } }}>
        <Search size={16} strokeWidth={2} className="scp__icon" />
        <input
          ref={inputRef}
          type="text"
          className="scp__input"
          placeholder={placeholder ?? "Que souhaitez-vous faire ?"}
          value={query}
          readOnly={isControlled}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {query && !isControlled ? (
          <button className="scp__clear" onClick={(e) => { e.stopPropagation(); setQuery(''); inputRef.current?.focus(); }}>
            <X size={13} strokeWidth={2.5} />
          </button>
        ) : !isControlled ? (
          <kbd className="scp__kbd">Ctrl+K</kbd>
        ) : null}
      </div>

      {open && hasResults && (
        <div className="scp__dropdown">
          <div className="scp__section">
            <div className="scp__section-label">
              <Sparkles size={10} strokeWidth={2.5} /> Propositions
            </div>
            {proposals.map((p, i) => {
              const Icon = ICON_MAP[p.icon] || Sparkles;
              return (
                <button
                  type="button"
                  key={p.id}
                  className={`scp__proposal${i === activeIdx ? ' scp__proposal--active' : ''}`}
                  onClick={() => handleProposalClick(p)}
                >
                  <div className="scp__proposal-icon"><Icon size={15} strokeWidth={2} /></div>
                  <div className="scp__proposal-body">
                    <span className="scp__proposal-label">{p.label}</span>
                    {p.desc && <span className="scp__proposal-desc">{p.desc}</span>}
                  </div>
                  <ChevronRight size={13} className="scp__proposal-arrow" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchCommandPalette;
